"use client";

import { ArrowDownRight, ArrowRight, ArrowUpRight, Clock, Minus } from "lucide-react";
import type {
	MarketTrend,
	MarketsExchangeContent,
	MarketsInflationContent,
	MarketsInterestContent,
} from "../../types";
import { RuntimeLink, useT } from "../../runtime/RendererRuntime";
import type { UiStringKey } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import {
	POLARITE_DU_CHANGE,
	TON_FLECHE,
	TON_PASTILLE,
	tonTendance,
	type IndicatorPolarity,
} from "./trendSemantics";

/**
 * Les trois cartes « marchés » (change, inflation, taux), extraites de
 * `MarketsBoardRenderer` pour être partagées avec la modale du bandeau
 * d'indicateurs clés.
 *
 * CE MODULE N'IMPORTE PAS `MarketsSparkline` — et ne doit jamais le faire.
 * La courbe tire recharts ; or la modale est montée dans le layout RACINE,
 * donc sur toutes les routes du site. L'importer ici ferait entrer recharts
 * dans le bundle de chaque page. La carte change reçoit donc son composant de
 * courbe en paramètre (`Sparkline`) : le tableau de bord d'accueil le fournit,
 * la modale non. Un test dédié (`markets-cards-recharts-free`) fige la règle.
 */
export type SparklineComponent = React.ComponentType<{
	values?: number[];
	labels?: string[];
	className?: string;
	compact?: boolean;
}>;

/** The projector bakes the featured deltas with a French period suffix
 * ("+0,38 % 1jr") — the number is data but the suffix is UI text. */
export function localizeDeltaSuffix(value: string, tr: (key: UiStringKey) => string): string {
	return value
		.replace(/\b1jr\b/, tr("deltaOneDaySuffix"))
		.replace(/\b2jrs\b/, tr("deltaTwoDaysSuffix"));
}

/* -------------------------------------------------------------------------- */
/* Card chrome                                                                */
/* -------------------------------------------------------------------------- */

/** Shared card. Ring-only surface (no drop shadow) so the cards sit flat on the
 *  tinted section background; drops the colored accent stripe and the meta
 *  "tag" pills used in the previous iteration. */
export function MarketCard({
	children,
	footer,
}: {
	children: React.ReactNode;
	footer?: React.ReactNode;
}) {
	return (
		<div className="flex h-full flex-col overflow-hidden rounded-md bg-white ring-1 ring-slate-200/80">
			<div className="flex-1 px-5 pt-4 pb-3 lg:px-7 lg:pt-6 lg:pb-5">{children}</div>
			{footer ? (
				<div className="border-t border-slate-100 px-5 py-2.5 lg:px-7 lg:py-4">{footer}</div>
			) : null}
		</div>
	);
}

/** Card heading + small meta line. Keeps the same hierarchy across cards
 *  so the eye lands on the title first, then the date, then the data. */
export function CardHeading({ title, asOf }: { title: string; asOf?: string }) {
	return (
		<div className="mb-3 flex items-baseline justify-between gap-2">
			<h4 className="text-[22px] md:text-[24px] xl:text-[26px] font-bold leading-tight text-[#005CA9]">
				{title}
			</h4>
			{asOf ? <p className="text-[11px] xl:text-[13px] text-slate-600">{asOf}</p> : null}
		</div>
	);
}

export function MoreLink({ label, href }: { label: string; href: string }) {
	return (
		<RuntimeLink
			href={href}
			className="group inline-flex items-center gap-1.5 text-[14px] md:text-[15px] lg:text-[16px] font-semibold text-[#0069B4] transition hover:text-[#005CA9]"
		>
			{label}
			<ArrowRight className="h-3.5 w-3.5 text-[#FFA401] transition group-hover:translate-x-0.5" />
		</RuntimeLink>
	);
}

/**
 * Flèche de variation. La flèche dit la DIRECTION, la couleur dit le SENS : elle n'est verte
 * que si le mouvement est une bonne nouvelle pour cet indicateur-là (cf. `trendSemantics`).
 * Sans polarité déclarée, la flèche reste grise : c'est le cas des taux que la Banque fixe.
 */
export function TrendArrow({
	trend,
	polarity = "neutre",
	className = "h-3.5 w-3.5",
}: {
	trend?: MarketTrend;
	polarity?: IndicatorPolarity;
	className?: string;
}) {
	const tr = useT();
	const color = TON_FLECHE[tonTendance(trend, polarity)];
	if (trend === "up") return <ArrowUpRight className={`${className} ${color}`} aria-label={tr("trendUp")} />;
	if (trend === "down") return <ArrowDownRight className={`${className} ${color}`} aria-label={tr("trendDown")} />;
	return <Minus className={`${className} text-slate-300`} aria-label={tr("trendStable")} />;
}

/* -------------------------------------------------------------------------- */
/* Exchange rates card                                                        */
/* -------------------------------------------------------------------------- */

export function ExchangeCard({
	data,
	actionLabel,
	actionHref,
	Sparkline,
}: {
	data: MarketsExchangeContent;
	actionLabel?: string;
	actionHref?: string;
	/** Composant de courbe injecté. Absent = aucune courbe (cf. en-tête du module). */
	Sparkline?: SparklineComponent;
}) {
	const tr = useT();
	const featured = data.featured;
	// Un seul point de réglage pour tout le site, cf. `POLARITE_DU_CHANGE`.
	const polarity: IndicatorPolarity = POLARITE_DU_CHANGE;
	// Tinted chip for the delta — clearer than colored text on white because
	// it groups the arrow + value into a single visual unit and lifts off the
	// surrounding card.
	const trendChip = TON_PASTILLE[tonTendance(featured?.trend, polarity)];
	const moreHref = resolveLinkHref(data.moreHref);
	const parallelHref = resolveLinkHref(data.parallelHref);
	const hasParallelLink = !!(parallelHref && data.parallelLabel);
	const hasMoreLink = !!(moreHref && data.moreLabel);
	// `rows` is injected by the deploy-time projector (fragment-resolved on the public
	// site). The admin preview renders the unresolved authoring shape, so rows can be
	// missing — default to [] and show a placeholder instead of crashing on `.map`.
	const rows = data.rows ?? [];

	return (
		<MarketCard
			footer={
				// Each link renders independently — earlier the whole footer
				// hinged on `moreHref && moreLabel`, so dropping "Plus de
				// détails" also silently hid "Cours parallèle". The two CTAs
				// answer different questions ("see another rate type" vs
				// "see the full table") and authors should be free to keep
				// either one alone.
				hasParallelLink || hasMoreLink ? (
					<div className="flex flex-wrap items-center justify-between gap-3">
						{hasParallelLink ? (
							<MoreLink label={data.parallelLabel as string} href={parallelHref as string} />
						) : (
							<span />
						)}
						{hasMoreLink ? <MoreLink label={data.moreLabel as string} href={moreHref as string} /> : null}
					</div>
				) : null
			}
		>
			{/* Date intentionally omitted from the heading — it lives next to
			    "cours indicatif" inside the featured panel below to read as
			    one piece of meta. */}
			<CardHeading title={data.title ?? tr("exchangeRates")} />

			{/* Body layout shifts at md only:
			    - mobile : single column (featured + sparkline-below + currency list)
			    - md     : 2-col grid — text on the left, full-height chart on the right
			    - lg+    : back to single column with compact sparkline next to featured
			    The card spans the full width at md (md:col-span-2 above) so the
			    50/50 split actually has room; at lg the card is half-width so the
			    big chart no longer makes sense. */}
			<div className="md:grid md:grid-cols-2 md:items-stretch md:gap-6 lg:block">
				<div>
					{featured ? (
						<div className="mb-4">
							{/* Rate kept smaller than the card title so the title stays
							    the primary anchor of the card. */}
							<div className="text-[16px] xl:text-[18px] font-bold leading-tight text-[#005CA9]">
								{featured.label}
							</div>

							{/* Trend chips on their own row so they don't collide with
							    the rate at narrow widths. delta1d is the primary movement;
							    delta2d (if present) sits next to it as a subdued companion. */}
							{(featured.delta1d || featured.delta2d) ? (
								<div className="mt-2 flex flex-wrap items-center gap-2">
									{featured.delta1d ? (
										<span
											className={`inline-flex items-center gap-1 whitespace-nowrap rounded-sm px-2 py-1 text-[11px] xl:text-[13px] font-semibold ${trendChip}`}
										>
											<TrendArrow trend={featured.trend} polarity={polarity} className="h-3 w-3" />
											{localizeDeltaSuffix(featured.delta1d, tr)}
										</span>
									) : null}
									{featured.delta2d ? (
										<span className="inline-flex items-center whitespace-nowrap rounded-sm bg-slate-100 px-2 py-1 text-[11px] xl:text-[13px] font-medium text-slate-600">
											{localizeDeltaSuffix(featured.delta2d, tr)}
										</span>
									) : null}
								</div>
							) : null}

							{/* Caption row pulls in the card-level date too: clock + date/
							    time on the left, "cours indicatif" italicised on the right.
							    Falls back to whichever of the two is set. */}
							{(data.asOf || featured.timestamp || featured.caption) ? (
								<div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] xl:text-[13px] text-slate-600">
									{data.asOf || featured.timestamp ? (
										<span className="inline-flex items-center gap-1.5">
											<Clock className="h-3 w-3" aria-hidden="true" />
											{data.asOf ?? featured.timestamp}
										</span>
									) : (
										<span />
									)}
									{featured.caption ? (
										<span className="italic text-slate-600">{featured.caption}</span>
									) : null}
								</div>
							) : null}

							{/* Mobile-only labelled chart. */}
							{Sparkline && data.sparkline ? (
								<Sparkline
									values={data.sparkline}
									labels={data.sparklineLabels}
									className="mt-3 block h-44 w-full md:hidden"
								/>
							) : null}
						</div>
					) : null}

					{rows.length === 0 ? (
						<div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-[12px] italic text-slate-500">
							{tr("marketsDynamicNote")}
						</div>
					) : (
					<ul className="divide-y divide-slate-100">
						{rows.map((r) => (
							<li key={r.code} className="flex items-center justify-between py-1.5 text-[14px] md:text-[15px] lg:text-[16px]">
								<span className="inline-flex items-center gap-2.5 font-semibold text-slate-700">
									{r.flag ? (
										<span
											className={`fi fi-${r.flag} text-[14px] xl:text-[16px]`}
											style={{ display: "inline-flex", boxShadow: "0px 1px 2px rgba(0,0,0,0.18)" }}
										/>
									) : null}
									{r.code}
								</span>
								<span className="inline-flex items-center gap-2 whitespace-nowrap text-slate-700">
									<span className="tabular-nums">{r.value}</span>
									<span className="text-[11px] xl:text-[13px] text-slate-600">CDF</span>
									<TrendArrow trend={r.trend} polarity={polarity} />
								</span>
							</li>
						))}
					</ul>
					)}
					{actionHref && actionLabel ? (
						<div className="mt-4 flex justify-center">
							<RuntimeLink
								href={actionHref}
								className="inline-flex items-center rounded-sm bg-[#FFA401] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-white shadow-sm transition hover:bg-[#E8930B]"
							>
								{actionLabel}
							</RuntimeLink>
						</div>
					) : null}
				</div>

				{/* Big chart — only visible at md (when card spans the full width). */}
				{Sparkline && data.sparkline ? (
					<div className="hidden md:flex md:items-stretch lg:hidden">
						<Sparkline
							values={data.sparkline}
							labels={data.sparklineLabels}
							className="block h-full min-h-[200px] w-full"
						/>
					</div>
				) : null}
			</div>
		</MarketCard>
	);
}

/* -------------------------------------------------------------------------- */
/* Inflation card                                                             */
/* -------------------------------------------------------------------------- */

export function InflationCard({ data }: { data: MarketsInflationContent }) {
	const tr = useT();
	const moreHref = resolveLinkHref(data.moreHref);
	const asOf = [data.asOf, data.source].filter(Boolean).join(" · ");
	// `rows` is normally injected by the deploy-time projector. The admin
	// preview renders the unresolved authoring shape, so rows can be missing
	// when the block is configured in dynamic mode.
	const rows = data.rows ?? [];
	const isDynamicUnresolved = data.mode === "dynamic" && rows.length === 0;
	return (
		<MarketCard
			footer={moreHref && data.moreLabel ? <MoreLink label={data.moreLabel} href={moreHref} /> : null}
		>
			<CardHeading title={data.title ?? tr("inflationRate")} />
			{/* Date a gauche derriere une horloge, mention d'unite en italique a droite : le meme
			    ordre que la carte des cours, pour que la bande se lise d'un seul balayage. */}
			{(data.unitCaption || asOf) ? (
				<div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] xl:text-[13px] text-slate-600">
					{asOf ? (
						<span className="inline-flex items-center gap-1.5">
							<Clock className="h-3 w-3" aria-hidden="true" />
							{asOf}
						</span>
					) : (
						<span />
					)}
					{data.unitCaption ? (
						<span className="italic text-slate-600">{data.unitCaption}</span>
					) : null}
				</div>
			) : null}
			{isDynamicUnresolved ? (
				<div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-[12px] italic text-slate-500">
					{tr("marketsDynamicNoteWithType").split("{type}")[0]}<code>{data.dynamicComponent?.contentTypeKey ?? ""}</code>{tr("marketsDynamicNoteWithType").split("{type}")[1]}
				</div>
			) : (
				<ul className="divide-y divide-slate-100">
					{rows.map((r) => (
						<li
							key={r.label}
							className="flex items-center justify-between gap-3 py-1.5 text-[14px] md:text-[15px] lg:text-[16px]"
						>
							<span className="text-slate-600">{r.label}</span>
							<span className="whitespace-nowrap font-bold tabular-nums text-[#005CA9]">{r.value}</span>
						</li>
					))}
				</ul>
			)}
		</MarketCard>
	);
}

/* -------------------------------------------------------------------------- */
/* Interest rates card                                                        */
/* -------------------------------------------------------------------------- */

export function InterestCard({ data }: { data: MarketsInterestContent }) {
	const tr = useT();
	const moreHref = resolveLinkHref(data.moreHref);
	return (
		<MarketCard
			footer={moreHref && data.moreLabel ? <MoreLink label={data.moreLabel} href={moreHref} /> : null}
		>
			<CardHeading title={data.title ?? tr("interestRate")} />

			{data.directeur ? (
				<section className="mb-4">
					<div className="flex items-baseline justify-between gap-2 text-[14px] md:text-[15px] lg:text-[16px]">
						<h5 className="font-semibold text-slate-700">
							{data.directeur.title ?? tr("policyRate")}
						</h5>
						{data.directeur.asOf ? (
							<span className="text-[11px] xl:text-[13px] text-slate-600">
								{(data.directeur.asOfLabel ?? tr("asOfLabel"))} {data.directeur.asOf}
							</span>
						) : null}
					</div>
					<ul className="mt-1 divide-y divide-slate-100">
						{(data.directeur.rows ?? []).map((r) => (
							<li
								key={r.label}
								className="flex items-center justify-between gap-3 py-1 text-[14px] md:text-[15px] lg:text-[16px]"
							>
								<span className="text-slate-600">{r.label}</span>
								<span className="inline-flex items-center gap-1.5 whitespace-nowrap font-bold tabular-nums text-[#005CA9]">
									{r.value}
									{/* Aucune polarité : la Banque FIXE ses taux. Une baisse est un
									    assouplissement, pas une contre-performance, et elle ne doit pas
									    s'afficher en rouge. La flèche suffit à dire le sens. */}
									<TrendArrow trend={r.trend} />
								</span>
							</li>
						))}
					</ul>
				</section>
			) : null}

			{data.bons ? (
				<section>
					<div className="flex items-baseline justify-between gap-2 text-[14px] md:text-[15px] lg:text-[16px]">
						<h5 className="font-semibold text-slate-700">
							{data.bons.title ?? tr("marketsBonsTitle")}
						</h5>
						{data.bons.asOf ? (
							<span className="text-[11px] xl:text-[13px] text-slate-600">
								{(data.bons.asOfLabel ?? tr("fromDate"))} {data.bons.asOf}
							</span>
						) : null}
					</div>
					<table className="mt-1 w-full border-collapse text-[12px] xl:text-[14px]">
						<thead>
							<tr className="text-left text-[10px] xl:text-[12px] uppercase tracking-wider text-slate-600">
								<th className="py-1 font-medium">{data.bons.columns?.jours ?? tr("days")}</th>
								<th className="py-1 text-right font-medium">{data.bons.columns?.tmp ?? tr("marketsTmpHeader")}</th>
								<th className="py-1 text-right font-medium">{data.bons.columns?.tm ?? tr("marketsTmHeader")}</th>
							</tr>
						</thead>
						<tbody>
							{(data.bons.rows ?? []).map((r) => (
								<tr key={r.jours} className="border-t border-slate-100">
									<td className="py-1.5 font-semibold text-slate-700 tabular-nums whitespace-nowrap">{r.jours}</td>
									<td className="py-1.5 text-right tabular-nums whitespace-nowrap text-[#005CA9]">{r.tmp}</td>
									<td className="py-1.5 text-right tabular-nums whitespace-nowrap text-[#005CA9]">{r.tm}</td>
								</tr>
							))}
						</tbody>
					</table>
				</section>
			) : null}
		</MarketCard>
	);
}
